/* SPEC-509 — the B mark set for the shell: one mark per nav GROUP heading and one per rail
 * control. Direction B, "line and solid node" (Param's pick, 07 Oct 2026): every mark is an
 * open line drawing in `currentColor` plus ONE solid node in `var(--mark-accent)` — the same
 * grammar as the SuryaMark (ink ring + amber disc). Path data is copied from the design
 * mockup `.agents/reference/design/navigation-redesign.html` (symbols g-* and r-*), on a
 * 24-unit grid, drawn at 1.5 strokes.
 *
 * Pure data, no React and no runtime imports, so `config/navMarks.test.ts` can assert it under
 * Node's type stripping. Rendering lives in `Mark.tsx`.
 *
 * Rules (frontend/src/CONTEXT.md, SPEC-456 + SPEC-509):
 *   - Marks sit on group HEADINGS and rail controls only. Never on a page row.
 *   - A mark is decorative (aria-hidden): the heading text or the control's aria-label is
 *     the accessible name.
 *   - No two marks in one set are the same drawing (the test checks it). */
import type { NavGroupLabel } from '../../../config/nav-groups';

export type MarkShape =
  | { el: 'path'; d: string }
  | { el: 'circle'; cx: number; cy: number; r: number }
  | { el: 'rect'; x: number; y: number; width: number; height: number; rx: number };

export interface MarkDrawing {
  /** Open line work, stroked in currentColor. */
  lines: MarkShape[];
  /** The solid node(s), filled with --mark-accent. Exactly the "one element" of direction B. */
  accent: MarkShape[];
}

export type GroupMarkId = NavGroupLabel | 'Favourites';

/** The shield from the "Trust & Governance" walk-through, so the group and its tour share one
 *  picture (the test asserts the line is identical to the tour glyph's). */
export const TRUST_SHIELD = 'M12 3l7 3v5c0 4.5-3 8-7 9.5C8 19 5 15.5 5 11V6z';

export const GROUP_MARKS: Record<GroupMarkId, MarkDrawing> = {
  // A trend line on an axis; the node is the forecast point ahead.
  Plan: {
    lines: [{ el: 'path', d: 'M4 4v16h16' }, { el: 'path', d: 'M7 15.5l3.2-3.4 3 1.9 3.2-3.6' }],
    accent: [{ el: 'circle', cx: 18.2, cy: 8.6, r: 2 }],
  },
  // A crate with stock dropping in; the node is the inbound wedge.
  Buy: {
    lines: [{ el: 'path', d: 'M4 11h16v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z' }, { el: 'path', d: 'M9.5 15h5' }],
    accent: [{ el: 'path', d: 'M8.6 3.6h6.8L12 8.2z' }],
  },
  // Goods leaving through the door; the node is the outbound arrowhead.
  Sell: {
    lines: [{ el: 'path', d: 'M11 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20H11' }, { el: 'path', d: 'M8.5 12h8.3' }],
    accent: [{ el: 'path', d: 'M16.6 8.3L21 12l-4.4 3.7z' }],
  },
  // A coin stack; the node is the coin you are protecting.
  Money: {
    lines: [
      { el: 'path', d: 'M4.5 7.2c0-1.5 3.2-2.7 7-2.7s7 1.2 7 2.7-3.2 2.7-7 2.7-7-1.2-7-2.7z' },
      { el: 'path', d: 'M4.5 7.2v4.6c0 1.4 2.6 2.5 6 2.7M18.5 7.2v3.1' },
      { el: 'path', d: 'M4.5 11.8v4.6c0 1.4 2.6 2.5 6 2.7' },
    ],
    accent: [{ el: 'circle', cx: 16.6, cy: 16.2, r: 3.6 }],
  },
  Trust: {
    lines: [{ el: 'path', d: TRUST_SHIELD }],
    accent: [{ el: 'circle', cx: 12, cy: 11.5, r: 2.4 }],
  },
  // Three sliders; the node is the one being set. The rail's Admin button reuses it.
  Admin: {
    lines: [
      { el: 'path', d: 'M4 6.5h8M16 6.5h4M4 17.5h2M10 17.5h10M4 12h10.6M19.4 12h.6' },
      { el: 'circle', cx: 14, cy: 6.5, r: 2 },
      { el: 'circle', cx: 8, cy: 17.5, r: 2 },
    ],
    accent: [{ el: 'circle', cx: 17, cy: 12, r: 2.3 }],
  },
  // An outline star with a solid centre. The row pin stays the plain lucide star (a control).
  Favourites: {
    lines: [{ el: 'path', d: 'M12 3.8l2.5 5 5.5.8-4 3.9.9 5.5L12 16.4 7.1 19l.9-5.5-4-3.9 5.5-.8z' }],
    accent: [{ el: 'circle', cx: 12, cy: 12.2, r: 1.9 }],
  },
};

export type RailMarkId =
  | 'menu' | 'search' | 'decisions' | 'trust' | 'help' | 'admin'
  | 'alerts' | 'tours' | 'feedback' | 'account' | 'ask';

export const RAIL_MARKS: Record<RailMarkId, MarkDrawing> = {
  menu: {
    lines: [{ el: 'rect', x: 3.5, y: 4.5, width: 17, height: 15, rx: 2 }],
    accent: [{ el: 'rect', x: 5.2, y: 6.2, width: 3.6, height: 11.6, rx: 0.8 }],
  },
  search: {
    lines: [{ el: 'circle', cx: 10.5, cy: 10.5, r: 6 }, { el: 'path', d: 'M15 15l5 5' }],
    accent: [{ el: 'circle', cx: 10.5, cy: 10.5, r: 2 }],
  },
  decisions: {
    lines: [{ el: 'path', d: 'M6 3.5h8.5l3.5 3.5v13.5H6z' }, { el: 'path', d: 'M9 9h6M9 12.3h4' }],
    accent: [{ el: 'circle', cx: 14.6, cy: 16.6, r: 2.3 }],
  },
  // SPEC-535: the Trust button reuses the Trust heading's shield — they are the same place
  // (the Admin button does the same with the Admin mark).
  trust: GROUP_MARKS.Trust,
  help: {
    lines: [{ el: 'circle', cx: 12, cy: 12, r: 8.5 }, { el: 'path', d: 'M9.6 9.6a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1.1.9-1.1 1.6v.4' }],
    accent: [{ el: 'circle', cx: 12, cy: 16.7, r: 1.25 }],
  },
  admin: GROUP_MARKS.Admin,
  alerts: {
    lines: [{ el: 'path', d: 'M6 16.5V11a6 6 0 0 1 12 0v5.5l1.6 2H4.4z' }],
    accent: [{ el: 'path', d: 'M9.8 20.2h4.4a2.2 2.2 0 0 1-4.4 0z' }],
  },
  // A compass with a solid needle.
  tours: {
    lines: [{ el: 'circle', cx: 12, cy: 12, r: 8.5 }],
    accent: [{ el: 'path', d: 'M15.6 8.4l-2 5.2-5.2 2 2-5.2z' }],
  },
  // A speech bubble whose tail is the node.
  feedback: {
    lines: [{ el: 'path', d: 'M8.5 15.5H5A1.5 1.5 0 0 1 3.5 14V6A1.5 1.5 0 0 1 5 4.5h14A1.5 1.5 0 0 1 20.5 6v8a1.5 1.5 0 0 1-1.5 1.5h-6.5' }],
    accent: [{ el: 'path', d: 'M8.5 14.6h4L8.5 19.6z' }],
  },
  account: {
    lines: [{ el: 'path', d: 'M5 20a7 7 0 0 1 14 0' }],
    accent: [{ el: 'circle', cx: 12, cy: 8.8, r: 3.6 }],
  },
  // The Ask-Anything mark: a speech bubble with the spark inside it.
  ask: {
    lines: [{ el: 'path', d: 'M4 5h16v11h-8l-4 3.5V16H4z' }],
    accent: [{ el: 'path', d: 'M12 6.9l1.05 2.75 2.75 1.05-2.75 1.05L12 14.5l-1.05-2.75L8.2 10.7l2.75-1.05z' }],
  },
};

/* SPEC-514 — the copilot drawer's header controls, in the same line-and-node grammar. */
export type DrawerMarkId = 'history' | 'clear' | 'expand' | 'collapse' | 'close' | 'back';

export const DRAWER_MARKS: Record<DrawerMarkId, MarkDrawing> = {
  // A clock face; the node is the hub the hands turn on.
  history: {
    lines: [{ el: 'circle', cx: 12, cy: 12, r: 8.5 }, { el: 'path', d: 'M12 6.8V12l3.4 2.2' }],
    accent: [{ el: 'circle', cx: 12, cy: 12, r: 1.6 }],
  },
  // A bin; the node is the lid's handle.
  clear: {
    lines: [{ el: 'path', d: 'M4.5 7h15M7 7l1 12.5h8L17 7' }, { el: 'path', d: 'M10.3 10.5v6M13.7 10.5v6' }],
    accent: [{ el: 'rect', x: 9.5, y: 3.8, width: 5, height: 2.4, rx: 0.8 }],
  },
  // Corners pulled outward round a solid centre.
  expand: {
    lines: [{ el: 'path', d: 'M14 4.5h5.5V10M10 19.5H4.5V14' }],
    accent: [{ el: 'rect', x: 9, y: 9, width: 6, height: 6, rx: 1 }],
  },
  // Corners pushed inward onto a smaller centre.
  collapse: {
    lines: [{ el: 'path', d: 'M19.5 9.5H14.5V4.5M4.5 14.5h5v5' }],
    accent: [{ el: 'rect', x: 10, y: 10, width: 4, height: 4, rx: 0.8 }],
  },
  // A panel whose right edge is the solid node: close the side panel.
  close: {
    lines: [{ el: 'rect', x: 3.5, y: 4.5, width: 17, height: 15, rx: 2 }, { el: 'path', d: 'M8 9.5l2.5 2.5L8 14.5' }],
    accent: [{ el: 'rect', x: 15.2, y: 6.2, width: 3.6, height: 11.6, rx: 0.8 }],
  },
  // An arrow home; the node is where you came from.
  back: {
    lines: [{ el: 'path', d: 'M18 12H6.5M10.5 7.5L6 12l4.5 4.5' }],
    accent: [{ el: 'circle', cx: 19, cy: 12, r: 1.6 }],
  },
};

/** A stable text key for a drawing — two marks with the same key are the same picture. */
export function drawingKey(m: MarkDrawing): string {
  return JSON.stringify([m.lines, m.accent]);
}
